import { ChangeDetectionStrategy, Component, OnInit, Signal, computed, inject, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Address } from '../../../core/models/address.model';
import { Appointment, AppointmentType } from '../../../core/models/appointment.model';
import {
  formatLineItemsSummary,
  Lead,
  LEAD_STAGES,
  LeadStage,
  parseLeadServices,
} from '../../../core/models/lead.model';
import { Tag } from '../../../core/models/tag.model';
import { AddressService } from '../../../core/services/address.service';
import { AppointmentService } from '../../../core/services/appointment.service';
import { BreadcrumbService } from '../../../core/services/breadcrumb.service';
import { LeadService } from '../../../core/services/lead.service';
import { LoggerService } from '../../../core/services/logger.service';
import { PermissionService } from '../../../core/services/permission.service';
import { NoopSchedulingProvider } from '../../../core/services/scheduling/noop-scheduling-provider';
import { TagService } from '../../../core/services/tag.service';
import { TranslationService } from '../../../core/services/translation.service';
import { OrganizationUser, UserService } from '../../../core/services/user.service';
import { AttachmentStripComponent } from '../../../shared/components/attachment-strip/attachment-strip.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { LeadStageChipComponent } from '../../../shared/components/lead-stage-chip/lead-stage-chip.component';
import { ModalDialogComponent } from '../../../shared/components/modal-dialog/modal-dialog.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { TagChipComponent } from '../../../shared/components/tag-chip/tag-chip.component';
import { DateTimezonePipe } from '../../../shared/pipes/date-timezone.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { exportToCSV, generateFilename } from '../../../shared/utils/export.utils';

interface AssigneeModel {
  assigned_to: string;
}

interface EditModel {
  stage: LeadStage;
  tag_ids: string[];
  notes: string;
}

interface AppointmentModel {
  appointment_type: AppointmentType;
  start: string;
  end: string;
  notes: string;
}

@Component({
  selector: 'app-lead-detail',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    FormlyModule,
    AttachmentStripComponent,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    LeadStageChipComponent,
    ModalDialogComponent,
    SpinnerComponent,
    TagChipComponent,
    DateTimezonePipe,
    TranslatePipe,
  ],
  templateUrl: './lead-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lead-detail.component.css',
})
export class LeadDetailComponent implements OnInit {
  private leadService = inject(LeadService);
  private addressService = inject(AddressService);
  private tagService = inject(TagService);
  private appointmentService = inject(AppointmentService);
  private schedulingProvider = inject(NoopSchedulingProvider);
  private userService = inject(UserService);
  private permissionService = inject(PermissionService);
  private breadcrumbService = inject(BreadcrumbService);
  private translate = inject(TranslationService);
  private route = inject(ActivatedRoute);
  private log = inject(LoggerService).getLogger('lead-detail');

  leadId = '';
  notFound = signal(false);

  lead: Signal<Lead | null> = signal(null);
  leadLoading: Signal<boolean> = signal(true);
  address = signal<Address | null>(null);
  /**
   * Safe defaults, not `!`-asserted: `lead` can turn truthy (from its own
   * reactive query) before these are wired up later in ngOnInit's async
   * chain, and the template reads all of them together once `lead()` is set.
   */
  tags: Signal<Tag[]> = signal([]);
  appointments: Signal<Appointment[]> = signal([]);
  users: Signal<OrganizationUser[]> = signal([]);

  userNames = computed(() => new Map(this.users().map((u) => [u.id, u.full_name || u.email])));
  lineItems = computed(() => (this.lead() ? parseLeadServices(this.lead()!) : []));
  servicesSummary = computed(() => formatLineItemsSummary(this.lineItems()));

  canAssign = this.permissionService.has('lead.assign');
  canUpdate = this.permissionService.has('lead.update');
  canBookAppointment = this.permissionService.has('appointment.create');

  assigneeForm = new FormGroup({});
  assigneeModel: AssigneeModel = { assigned_to: '' };
  assigneeFields: FormlyFieldConfig[] = [];

  editForm = new FormGroup({});
  editModel: EditModel = { stage: 'new', tag_ids: [], notes: '' };
  editFields: FormlyFieldConfig[] = [];
  savingEdit = signal(false);

  showAppointmentModal = signal(false);
  appointmentForm = new FormGroup({});
  appointmentModel: AppointmentModel = { appointment_type: 'estimate', start: '', end: '', notes: '' };
  appointmentFields: FormlyFieldConfig[] = [];
  bookingAppointment = signal(false);

  async ngOnInit(): Promise<void> {
    this.leadId = this.route.snapshot.paramMap.get('id') ?? '';

    const leadQuery = this.leadService.createQueryById(this.leadId);
    this.lead = leadQuery.data;
    this.leadLoading = leadQuery.loading;

    const lead = await this.leadService.getById(this.leadId);
    if (!lead) {
      this.notFound.set(true);
      return;
    }

    this.address.set(await this.addressService.getById(lead.address_id));
    this.tags = this.tagService.createForLeadQuery(this.leadId).data;
    this.appointments = this.appointmentService.createQueryForLead(this.leadId).data;
    this.users = this.userService.createQuery(false).data;

    this.breadcrumbService.setDynamicLabel(lead.contact_name || this.translate.translate('leads.detail.untitled'));

    this.assigneeModel = { assigned_to: lead.assigned_to };
    const tagIds = await this.tagService.getIdsForLead(this.leadId);
    this.editModel = { stage: lead.stage, tag_ids: tagIds, notes: lead.notes };

    this.buildFields();

    this.assigneeForm.valueChanges.subscribe(async () => {
      try {
        await this.leadService.update({ id: this.leadId, assigned_to: this.assigneeModel.assigned_to });
      } catch (err) {
        this.log.error('Failed to reassign lead', err);
      }
    });
  }

  private buildFields(): void {
    const t = (key: string) => this.translate.translate(key);

    this.assigneeFields = [
      {
        key: 'assigned_to',
        type: 'native-select',
        props: {
          label: t('leads.detail.assignedTo'),
          showLabel: true,
          options: this.users().map((u) => ({ value: u.id, label: u.full_name || u.email })),
        },
      },
    ];

    const activeTags = this.tags();
    this.editFields = [
      {
        key: 'stage',
        type: 'native-select',
        props: {
          label: t('leads.detail.stage'),
          showLabel: true,
          options: LEAD_STAGES.map((s) => ({ value: s, label: t(`leads.stage.${s}`) })),
        },
      },
      ...(activeTags.length > 0
        ? [
            {
              key: 'tag_ids',
              type: 'multi-checkbox',
              props: { label: t('interactions.form.tagsLabel'), options: activeTags.map((tag) => ({ value: tag.id, label: tag.label })) },
            },
          ]
        : []),
      { key: 'notes', type: 'textarea', props: { label: t('leads.detail.notesLabel') } },
    ];

    this.appointmentFields = [
      {
        key: 'appointment_type',
        type: 'native-select',
        props: {
          label: t('interactions.form.appointment.typeLabel'),
          showLabel: true,
          options: [
            { value: 'estimate', label: t('interactions.form.appointment.type.estimate') },
            { value: 'service_call', label: t('interactions.form.appointment.type.serviceCall') },
            { value: 'follow_up', label: t('interactions.form.appointment.type.followUp') },
          ],
        },
      },
      { key: 'start', type: 'datetime', props: { label: t('interactions.form.appointment.startLabel') } },
      { key: 'end', type: 'datetime', props: { label: t('interactions.form.appointment.endLabel') } },
      { key: 'notes', type: 'textarea', props: { label: t('interactions.form.appointment.notesLabel') } },
    ];
  }

  async saveEdit(): Promise<void> {
    this.savingEdit.set(true);
    try {
      await this.leadService.update({ id: this.leadId, stage: this.editModel.stage, notes: this.editModel.notes });
      await this.tagService.saveForLead(this.leadId, this.editModel.tag_ids);
    } catch (err) {
      this.log.error('Failed to save lead edits', err);
    } finally {
      this.savingEdit.set(false);
    }
  }

  openBookAppointment(): void {
    this.appointmentModel = { appointment_type: 'estimate', start: '', end: '', notes: '' };
    this.showAppointmentModal.set(true);
  }

  closeBookAppointment(): void {
    this.showAppointmentModal.set(false);
  }

  async bookAppointment(): Promise<void> {
    const start = this.fromLocalInput(this.appointmentModel.start);
    if (!start) return;
    const end = this.fromLocalInput(this.appointmentModel.end) ?? start;

    this.bookingAppointment.set(true);
    try {
      await this.schedulingProvider.book({
        lead_id: this.leadId,
        address_id: this.address()?.id ?? '',
        appointment_type: this.appointmentModel.appointment_type,
        scheduled_start: start,
        scheduled_end: end,
        notes: this.appointmentModel.notes,
      });
      this.showAppointmentModal.set(false);
    } catch (err) {
      this.log.error('Failed to book appointment', err);
    } finally {
      this.bookingAppointment.set(false);
    }
  }

  exportLead(): void {
    const row = this.exportRow();
    const lead = this.lead();
    if (!row || !lead) return;
    exportToCSV([row], generateFilename('lead', lead.contact_name));
  }

  private exportRow(): Record<string, string | number> | null {
    const lead = this.lead();
    if (!lead) return null;
    return {
      Contact: lead.contact_name,
      Phone: lead.contact_phone,
      Email: lead.contact_email,
      Address: this.address()?.formatted_address ?? '',
      Stage: lead.stage,
      'Assigned to': this.userNames().get(lead.assigned_to) ?? '',
      Services: this.servicesSummary(),
      'Estimated value': lead.estimated_value ?? '',
      Currency: lead.currency,
      Notes: lead.notes,
    };
  }

  private fromLocalInput(value: string): number | null {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
}
